import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Plus, ChevronDown, ChevronUp, History, Copy, Check } from 'lucide-react';
import TokenPurchaseModal from './TokenPurchaseModal';

export default function WalletWidget({ userId, compact = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Mock wallet data - in production, fetch from UserWallet entity
  const walletData = {
    address: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb',
    balance: 150,
    recentTransactions: [
      { id: 1, type: 'purchase', amount: 100, date: '2024-12-01', status: 'completed' },
      { id: 2, type: 'earned', amount: 50, date: '2024-11-28', status: 'completed' },
    ]
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(walletData.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (compact) {
    return (
      <>
        <button
          onClick={() => setShowPurchaseModal(true)}
          className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#8B5CF6] to-[#6366F1] hover:from-[#7C3AED] hover:to-[#4F46E5] rounded-lg text-white text-sm font-medium transition-all"
        >
          <Wallet className="w-4 h-4" />
          <span>{walletData.balance} RLY</span>
          <Plus className="w-3 h-3" />
        </button>

        <TokenPurchaseModal
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          userId={userId}
          existingWallet={walletData.address}
        />
      </>
    );
  }

  return (
    <>
      <div className="bg-[#1F2937] rounded-xl border border-white/10 overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#8B5CF6] to-[#6366F1] rounded-xl flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-400">Your Wallet</p>
              <p className="text-xl font-bold text-white">{walletData.balance} RLY</p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 pt-0 space-y-4">
                {/* Buy Button */}
                <button
                  onClick={() => setShowPurchaseModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#00c853] hover:from-[#7C3AED] hover:to-[#00b348] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Buy More Tokens
                </button>

                {/* Wallet Address */}
                <div className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Wallet Address</span>
                    <button
                      onClick={handleCopyAddress}
                      className="text-xs text-[#8B5CF6] hover:text-[#A78BFA] flex items-center gap-1"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <code className="text-sm text-gray-300 font-mono break-all">
                    {walletData.address.slice(0, 10)}...{walletData.address.slice(-8)}
                  </code>
                </div>

                {/* Recent Transactions */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <History className="w-4 h-4" />
                      Recent Activity
                    </span>
                  </div>
                  <div className="space-y-2">
                    {walletData.recentTransactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                        <div>
                          <p className="text-sm text-white capitalize">{tx.type}</p>
                          <p className="text-xs text-gray-500">{tx.date}</p>
                        </div>
                        <span className={`text-sm font-medium ${
                          tx.type === 'purchase' ? 'text-[#8B5CF6]' : 'text-green-400'
                        }`}>
                          +{tx.amount} RLY
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <TokenPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        userId={userId}
        existingWallet={walletData.address}
      />
    </>
  );
}
